import * as THREE from 'three/webgpu';
import type { FeatureFace } from '../../mesh/feature/FeatureFace';
import {
    isEdgeAwareSelectionMode,
    type EntitySelectionMode,
} from './EntitySelectionMode';

/** 草图候选与实体候选判定为“同距离”的默认容差。 */
export const SKETCH_SELECTION_TIE_DISTANCE_EPSILON = 1e-4;
/** 面边混合模式下，允许边界线提升为主命中的最大屏幕距离（像素）。 */
export const FEATURE_EDGE_PRIORITY_SCREEN_DISTANCE_PX = 4;

/**
 * 读取屏幕空间命中的像素距离。
 *
 * @param hit 命中结果。
 * @returns 像素距离；缺失时返回 `null`。
 */
function resolveScreenDistance(
    hit: THREE.Intersection<THREE.Object3D> | THREE.Intersection<FeatureFace> | null,
): number | null {
    const value = hit ? Reflect.get(hit, 'screenDistance') : null;
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * 判断面边混合模式下的边界线兜底是否有效。
 *
 * @param faceBoundaryLineHit 当前面派生的边界线命中。
 * @returns 是否足够接近当前光标。
 */
function isBoundaryHitEligible(
    faceBoundaryLineHit: THREE.Intersection<THREE.Object3D> | null,
): boolean {
    const boundaryScreenDistance = resolveScreenDistance(faceBoundaryLineHit);
    return boundaryScreenDistance !== null
        && boundaryScreenDistance <= FEATURE_EDGE_PRIORITY_SCREEN_DISTANCE_PX;
}

/**
 * 判断 object 模式下的顶点命中是否允许优先于前景面/mesh。
 *
 * 设计说明：
 * 1. SolidWorks 默认对象选择在光标准确指向顶点时会优先选择点；
 * 2. 但被前景面遮挡的背后顶点不能抢走前景面，否则装配体内隐藏拓扑会误触发；
 * 3. 因此这里只允许“与前景命中同距或更近”的点提升优先级，兼顾顶点拾取与遮挡语义。
 *
 * @param pointHit 顶点命中。
 * @param faceHit 当前前景面命中。
 * @param meshHit 当前 mesh 命中。
 * @returns 顶点是否可作为 object 模式主命中。
 */
function isObjectModePointHitEligible(
    pointHit: THREE.Intersection<THREE.Object3D> | null,
    faceHit: THREE.Intersection<FeatureFace> | null,
    meshHit: THREE.Intersection<THREE.Object3D> | null,
): boolean {
    if (!pointHit) {
        return false;
    }

    const foregroundDistance = Math.min(
        faceHit?.distance ?? Number.POSITIVE_INFINITY,
        meshHit?.distance ?? Number.POSITIVE_INFINITY,
    );

    if (!Number.isFinite(foregroundDistance)) {
        return true;
    }

    return pointHit.distance <= foregroundDistance + SKETCH_SELECTION_TIE_DISTANCE_EPSILON;
}

/**
 * 在前景面已命中的前提下解析 `face-edge` 模式主命中。
 *
 * 设计说明：
 * 1. `face-edge` 的 SolidWorks 语义是“离当前前景面的边界足够近则选边，否则选面”；
 * 2. 因此全局 `lineHit` 不能在边界线不满足阈值时直接绕过面命中，否则隐藏边会抢走前景面；
 * 3. 只有当全局边命中与当前面边界线命中都存在，且全局边在屏幕距离上不更差时，才允许它作为权威边命中胜出。
 *
 * @param pointHit 点命中。
 * @param lineHit 全局边命中。
 * @param faceBoundaryLineHit 当前面派生的边界线命中。
 * @param faceHit 当前前景面命中。
 * @param meshHit 当前实体 mesh 命中。
 * @returns `face-edge` 模式下的主命中。
 */
function resolveFaceEdgeModeHit(input: {
    pointHit: THREE.Intersection<THREE.Object3D> | null;
    lineHit: THREE.Intersection<THREE.Object3D> | null;
    faceBoundaryLineHit: THREE.Intersection<THREE.Object3D> | null;
    faceHit: THREE.Intersection<FeatureFace>;
    meshHit: THREE.Intersection<THREE.Object3D> | null;
}): THREE.Intersection<THREE.Object3D> | null {
    if (isBoundaryHitEligible(input.faceBoundaryLineHit)) {
        /**
         * `face-edge` 的底层语义应该是“先确定当前前景面，再只在该面的合法边界线上升级为边”。
         *
         * 旧实现允许全局 `lineHit` 在这里反向覆盖 `faceBoundaryLineHit`，会把已经按前景面局部语义选出的边
         * 再次退化回全局最近线段竞争，导致点上边时仍可能被同面或异面的其他边抢走。
         */
        return input.faceBoundaryLineHit;
    }

    return resolveNearestHitWithTieBreak([
        { hit: input.pointHit, priority: 0 },
        { hit: input.faceHit, priority: 3 },
        { hit: input.meshHit, priority: 4 },
    ]);
}

/**
 * 解析草图域的主命中结果。
 *
 * @param input 草图域命中输入。
 * @returns 草图域主命中结果；无命中时返回 `null`。
 */
export function resolvePrimarySketchHit(input: {
    pointHit: THREE.Intersection<THREE.Object3D> | null;
    lineHit: THREE.Intersection<THREE.Object3D> | null;
    contourHit: THREE.Intersection<THREE.Object3D> | null;
}): THREE.Intersection<THREE.Object3D> | null {
    return resolveNearestHitWithTieBreak([
        { hit: input.pointHit, priority: 0 },
        { hit: input.lineHit, priority: 1 },
        { hit: input.contourHit, priority: 2 },
    ]);
}

/**
 * 解析实体域的主命中结果。
 *
 * @param input 实体域命中输入。
 * @returns 实体域主命中结果；无命中时返回 `null`。
 */
export function resolvePrimaryEntityHit(input: {
    pointHit: THREE.Intersection<THREE.Object3D> | null;
    lineHit: THREE.Intersection<THREE.Object3D> | null;
    faceBoundaryLineHit: THREE.Intersection<THREE.Object3D> | null;
    faceHit: THREE.Intersection<FeatureFace> | null;
    meshHit: THREE.Intersection<THREE.Object3D> | null;
    selectionMode?: EntitySelectionMode;
}): THREE.Intersection<THREE.Object3D> | null {
    if (input.selectionMode === 'topology') {
        if (isObjectModePointHitEligible(input.pointHit, input.faceHit, input.meshHit)) {
            return input.pointHit;
        }
        if (input.lineHit) {
            return input.lineHit;
        }
        if (isBoundaryHitEligible(input.faceBoundaryLineHit)) {
            return input.faceBoundaryLineHit;
        }
        return resolveNearestHitWithTieBreak([
            { hit: input.faceHit, priority: 3 },
            { hit: input.meshHit, priority: 4 },
        ]);
    }

    if (input.selectionMode === 'face-edge' && input.faceHit) {
        return resolveFaceEdgeModeHit({
            pointHit: input.pointHit,
            lineHit: input.lineHit,
            faceBoundaryLineHit: input.faceBoundaryLineHit,
            faceHit: input.faceHit,
            meshHit: input.meshHit,
        });
    }

    if ((input.selectionMode ?? 'object') === 'object') {
        if (isObjectModePointHitEligible(input.pointHit, input.faceHit, input.meshHit)) {
            return input.pointHit;
        }

        if (isBoundaryHitEligible(input.faceBoundaryLineHit)) {
            /**
             * 默认对象模式同样需要 SolidWorks 式“贴近前景面边界则选边，否则选面”语义。
             * 斜面命中点通常比边界线投影点更靠近射线起点，如果继续只按 ray distance 仲裁，
             * 鼠标贴着斜面上下边缘时会稳定被面抢走，导致 FeatureLine 无法 hover/选中。
             */
            return input.faceBoundaryLineHit;
        }
    }

    if (isEdgeAwareSelectionMode(input.selectionMode ?? 'object')) {
        if (input.lineHit) {
            return input.lineHit;
        }
        if (isBoundaryHitEligible(input.faceBoundaryLineHit)) {
            return input.faceBoundaryLineHit;
        }
    }

    return resolveNearestHitWithTieBreak([
        { hit: input.pointHit, priority: 0 },
        { hit: input.lineHit, priority: 1 },
        { hit: input.faceBoundaryLineHit, priority: 2 },
        { hit: input.faceHit, priority: 3 },
        { hit: input.meshHit, priority: 4 },
    ]);
}

/**
 * 在草图域与实体域之间解析最终命中结果。
 *
 * @param input 域间裁决输入。
 * @returns 最终命中结果；无命中时返回 `null`。
 */
export function resolveSelectionDomainWinner(input: {
    sketchHit: THREE.Intersection<THREE.Object3D> | null;
    entityHit: THREE.Intersection<THREE.Object3D> | null;
    distanceEpsilon?: number;
}): THREE.Intersection<THREE.Object3D> | null {
    if (input.sketchHit && input.entityHit) {
        const epsilon = Math.max(0, input.distanceEpsilon ?? SKETCH_SELECTION_TIE_DISTANCE_EPSILON);
        const distanceDelta = input.sketchHit.distance - input.entityHit.distance;

        if (distanceDelta <= epsilon) {
            return input.sketchHit;
        }

        return input.entityHit;
    }

    return input.sketchHit ?? input.entityHit;
}

/**
 * 在同一选择域内按距离解析最近命中。
 *
 * @param candidates 带优先级的候选命中列表。
 * @returns 域内主命中；无候选时返回 `null`。
 */
function resolveNearestHitWithTieBreak(candidates: Array<{
    hit: THREE.Intersection<THREE.Object3D> | THREE.Intersection<FeatureFace> | null;
    priority: number;
}>): THREE.Intersection<THREE.Object3D> | null {
    let bestHit: THREE.Intersection<THREE.Object3D> | null = null;
    let bestPriority = Number.POSITIVE_INFINITY;

    candidates.forEach((candidate) => {
        if (!candidate.hit) {
            return;
        }

        if (!bestHit) {
            bestHit = candidate.hit as THREE.Intersection<THREE.Object3D>;
            bestPriority = candidate.priority;
            return;
        }

        const distanceDelta = candidate.hit.distance - bestHit.distance;
        if (distanceDelta < -SKETCH_SELECTION_TIE_DISTANCE_EPSILON) {
            bestHit = candidate.hit as THREE.Intersection<THREE.Object3D>;
            bestPriority = candidate.priority;
            return;
        }

        if (
            Math.abs(distanceDelta) <= SKETCH_SELECTION_TIE_DISTANCE_EPSILON
            && candidate.priority < bestPriority
        ) {
            bestHit = candidate.hit as THREE.Intersection<THREE.Object3D>;
            bestPriority = candidate.priority;
        }
    });

    return bestHit;
}
